import React, { useState, useContext, useEffect } from 'react';
import { Grid, TextField, DialogActions, Button, MenuItem, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';
import { AlertMessagesContext } from 'react-alert-messages';
import VariantService from 'services/VariantService';
import { useTranslation } from 'react-i18next';

export default function VariantLabelEditDialog({ open, label, variantOptions = [], handleClose, handleSuccess }) {
  const [data, setData] = useState({ variantId: '', name: '' });
  const [isLoading, setLoading] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  useEffect(() => {
    if (open && label) {
      setData({
        variantId: label.dimension?.id || '',
        name: label.name || '',
      });
    }
  }, [open, label]);

  if (!open || !label) return null;

  const noChanges = data.variantId === (label.dimension?.id || '') && data.name.trim() === (label.name || '');

  const handleUpdateLabel = async () => {
    if (!data.variantId || !data.name.trim()) return;

    setLoading(true);
    try {
      const updated = await VariantService.updateVariantLabel(label.id, {
        dimension_id: data.variantId,
        name: data.name.trim(),
      });
      postAlertMessage({
        type: 'success',
        text: tPopup('Label "{{label}}" updated successfully.', { label: data.name.trim() }),
      });
      handleSuccess(updated);
    } catch (err) {
      postAlertMessage({
        type: 'error',
        text: err.message || tPopup('Failed to update variant label.'),
      });
      handleClose();
    }
    setLoading(false);
  };

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{tCommon('Edit Variant Label')}</h2>
            <CloseIcon onClick={handleClose} className={styles.closeBtn} />
          </div>

          <div className={styles.inputSec}>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Select Variant')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  select
                  label={tCommon('Variant')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={data.variantId}
                  onChange={(e) => setData({ ...data, variantId: e.target.value })}
                  disabled={isLoading}
                >
                  {variantOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Variant Label Name')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  label={tCommon('Variant Label Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose} disabled={isLoading}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                disabled={isLoading || noChanges}
                onClick={handleUpdateLabel}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {tCommon('Updating...')}
                  </>
                ) : (
                  tCommon('Update')
                )}
              </Button>
            </DialogActions>
          </div>
        </div>
      </div>
    </>
  );
}
